import React, { FC, useState, useRef, useEffect } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import { CommentsAnnotation } from "@/types/types";
import { ZoomIn, ZoomOut, ChevronLeft, ChevronRight } from "lucide-react";
import CustomReactMarkdown from "./CustomReactMarkdown";
import BrandPane from "./BrandPane";
import NavigationPane from "./NavigationPane";

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
    "pdfjs-dist/build/pdf.worker.min.mjs",
    import.meta.url
).toString();

interface CommentValidationViewProps {
    pdfOriginal: string;
    pdfUpdated: string;
    annotations: CommentsAnnotation[];
}

const CommentValidationView: FC<CommentValidationViewProps> = ({
    pdfOriginal,
    pdfUpdated,
    annotations,
}) => {
    const [numPages, setNumPages] = useState<number | null>(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [scale, setScale] = useState(1.0);
    const [selectedAnnotation, setSelectedAnnotation] =
        useState<CommentsAnnotation | null>(null);
    const [initialScaleSet, setInitialScaleSet] = useState(false);
    const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
    const [isFilterOpen, setIsFilterOpen] = useState(false);

    const beforeContainerRef = useRef<HTMLDivElement>(null);
    const afterContainerRef = useRef<HTMLDivElement>(null);
    const annotationRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});

    const calculateIdealScale = (page: any) => {
        if (!beforeContainerRef.current) return 1.0;

        const containerWidth = beforeContainerRef.current.clientWidth - 40;
        const containerHeight = beforeContainerRef.current.clientHeight - 160;

        const pageWidth = page.originalWidth;
        const pageHeight = page.originalHeight;

        const scaleX = containerWidth / pageWidth;
        const scaleY = containerHeight / pageHeight;

        return Math.min(Math.min(scaleX, scaleY) * 0.95, 1.5);
    };

    const zoomIn = () => setScale((prev) => Math.min(prev + 0.1, 2));
    const zoomOut = () => setScale((prev) => Math.max(prev - 0.1, 0.3));

    const onPageLoadSuccess = (page: any) => {
        if (!initialScaleSet) {
            const newScale = calculateIdealScale(page);
            setScale(newScale);
            setInitialScaleSet(true);
        }
    };

    const handleBoxClick = (
        e: React.MouseEvent,
        annotation: CommentsAnnotation
    ) => {
        e.preventDefault();
        e.stopPropagation();
        setSelectedAnnotation(annotation);
    };

    const handleDotClick = (
        e: React.MouseEvent,
        annotation: CommentsAnnotation
    ) => {
        e.preventDefault();
        e.stopPropagation();
        setSelectedAnnotation(annotation);

        const annotationElement = annotationRefs.current[annotation.id];
        if (annotationElement) {
            annotationElement.scrollIntoView({
                behavior: "smooth",
                block: "start",
            });
        }
    };

    const getBadgeColor = (status: string) => {
        if (status === "DISCREPANCY") {
            return "bg-blue-100 text-blue-800";
        }
        switch (status) {
            case "PARTIALLY ADDRESSED":
                return "bg-yellow-100 text-yellow-800";
            case "NOT ADDRESSED":
                return "bg-red-100 text-red-800";
            case "FULLY ADDRESSED":
                return "bg-green-100 text-green-800";
            default:
                return "bg-gray-100 text-gray-800";
        }
    };

    const getCircleColor = (status: string) => {
        if (status === "DISCREPANCY") {
            return "bg-blue-500";
        }
        switch (status) {
            case "PARTIALLY ADDRESSED":
                return "bg-yellow-500";
            case "NOT ADDRESSED":
                return "bg-red-500";
            case "FULLY ADDRESSED":
                return "bg-green-500";
            default:
                return "bg-gray-500";
        }
    };

    const handlePageChange = (offset: number) => {
        setCurrentPage((prevPage) =>
            Math.min(Math.max(prevPage + offset, 1), numPages || 1)
        );
    };

    const REVIEW_STATUSES = [
        "FULLY CAPTURED",
        "PARTIALLY CAPTURED",
        "NOT CAPTURED",
        "DISCREPANCY",
    ];

    const filteredAnnotations = annotations.filter(
        (annotation) =>
            selectedStatuses.length === 0 ||
            selectedStatuses.includes(annotation.reviewStatus)
    );

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as HTMLElement;
            if (!target.closest(".filter-dropdown")) {
                setIsFilterOpen(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () =>
            document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    return (
        <div className="flex h-screen bg-gray-100">
            {/* Brand Pane */}
            <BrandPane />

            {/* Navigation Pane */}
            <NavigationPane />

            {/* Main content - wrap your existing content */}
            <div className="flex-1 overflow-hidden flex flex-col">
                {/* Header */}
                <div className="p-6 flex justify-end bg-white">
                    <img
                        src="/synthio_logo_with_text.png"
                        alt="SynthioLabs Logo"
                        className="h-8 w-auto"
                    />
                </div>
                <div className="flex flex-1 bg-gray-100 p-4 relative">
                    <div className="flex w-full h-full bg-white rounded-lg shadow-lg relative">
                        {/* Before PDF Viewer */}
                        <div
                            ref={beforeContainerRef}
                            className="flex-1 h-full overflow-hidden border-r relative flex flex-col"
                        >
                            <div className="sticky top-4 z-20 w-full px-4">
                                <div className="flex justify-between items-center h-[52px] mb-8 mx-8">
                                    <h2 className="text-xl font-semibold">
                                        Draft with Comments
                                    </h2>
                                    <div className="w-[104px]"></div>{" "}
                                    {/* Spacer matching zoom buttons width */}
                                </div>
                            </div>

                            {/* Main content wrapper */}
                            <div className="flex-1 overflow-auto pb-16">
                                <Document
                                    file={pdfOriginal}
                                    onLoadSuccess={({ numPages }) =>
                                        setNumPages(numPages)
                                    }
                                    className="flex flex-col items-center min-w-fit"
                                >
                                    <Page
                                        pageNumber={currentPage}
                                        scale={scale}
                                        className="shadow-md relative"
                                        onLoadSuccess={onPageLoadSuccess}
                                    >
                                        {/* Annotations overlay for original boxes */}
                                        <div className="absolute top-0 left-0 w-full h-full pointer-events-none">
                                            {annotations
                                                .filter(
                                                    (ann) =>
                                                        ann.page === currentPage
                                                )
                                                .map((annotation, index) => {
                                                    return annotation.boundingBoxOriginal.map(
                                                        (box, boxIndex) => (
                                                            <div
                                                                key={`${annotation.id}-${boxIndex}`}
                                                                className="absolute pointer-events-auto"
                                                                style={{
                                                                    left: `${
                                                                        box.x -
                                                                        box.width /
                                                                            2 -
                                                                        0.5
                                                                    }%`,
                                                                    top: `${
                                                                        box.y -
                                                                        box.height /
                                                                            2 -
                                                                        0.5
                                                                    }%`,
                                                                    width: `${
                                                                        box.width +
                                                                        1
                                                                    }%`,
                                                                    height: `${
                                                                        box.height +
                                                                        1
                                                                    }%`,
                                                                }}
                                                            >
                                                                {selectedAnnotation?.id ===
                                                                    annotation.id && (
                                                                    <div className="absolute inset-0">
                                                                        <span className="relative flex h-full w-full">
                                                                            <span
                                                                                className="absolute inline-flex h-full w-full border-2 border-blue-800 bg-blue-500/10 opacity-75 cursor-pointer"
                                                                                onClick={(
                                                                                    e
                                                                                ) =>
                                                                                    handleBoxClick(
                                                                                        e,
                                                                                        annotation
                                                                                    )
                                                                                }
                                                                            ></span>
                                                                        </span>
                                                                    </div>
                                                                )}
                                                                <div
                                                                    className={`absolute w-6 h-6 flex items-center justify-center text-white font-bold rounded-full cursor-pointer z-10 
                                                                    ${
                                                                        selectedAnnotation?.id ===
                                                                        annotation.id
                                                                            ? "bg-gray-400"
                                                                            : getCircleColor(
                                                                                  annotation.reviewStatus
                                                                              )
                                                                    }`}
                                                                    style={{
                                                                        top: "-8px",
                                                                        left: `${
                                                                            4 +
                                                                            index *
                                                                                6
                                                                        }px`,
                                                                        transform:
                                                                            "translate(-50%, -50%)",
                                                                    }}
                                                                    onClick={(
                                                                        e
                                                                    ) =>
                                                                        handleDotClick(
                                                                            e,
                                                                            annotation
                                                                        )
                                                                    }
                                                                >
                                                                    <span className="text-xs">
                                                                        {index +
                                                                            1}
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        )
                                                    );
                                                })}
                                        </div>
                                    </Page>
                                </Document>
                            </div>
                        </div>

                        {/* After PDF Viewer */}
                        <div
                            ref={afterContainerRef}
                            className="flex-1 h-full overflow-hidden border-r relative flex flex-col"
                        >
                            <div className="sticky top-4 z-20 w-full px-4">
                                <div className="flex justify-between items-center h-[52px] mb-8 mx-8">
                                    <h2 className="text-xl font-semibold">
                                        Updated Draft
                                    </h2>
                                    <div className="flex gap-2">
                                        <button
                                            onClick={zoomOut}
                                            className="p-2 bg-white rounded-full shadow-md hover:bg-gray-100"
                                            title="Zoom Out"
                                        >
                                            <ZoomOut size={20} />
                                        </button>
                                        <button
                                            onClick={zoomIn}
                                            className="p-2 bg-white rounded-full shadow-md hover:bg-gray-100"
                                            title="Zoom In"
                                        >
                                            <ZoomIn size={20} />
                                        </button>
                                    </div>
                                </div>
                            </div>

                            {/* Main content wrapper */}
                            <div className="flex-1 overflow-auto pb-16">
                                <Document
                                    file={pdfUpdated}
                                    className="flex flex-col items-center min-w-fit"
                                >
                                    <Page
                                        pageNumber={currentPage}
                                        scale={scale}
                                        className="shadow-md relative"
                                    >
                                        {/* Annotations overlay for updated boxes */}
                                        <div className="absolute top-0 left-0 w-full h-full pointer-events-none">
                                            {annotations
                                                .filter(
                                                    (ann) =>
                                                        ann.page === currentPage
                                                )
                                                .map((annotation, index) =>
                                                    annotation.boundingBoxUpdated.map(
                                                        (box, boxIndex) => (
                                                            <div
                                                                key={`${annotation.id}-${boxIndex}`}
                                                                className="absolute pointer-events-auto"
                                                                style={{
                                                                    left: `${
                                                                        box.x -
                                                                        box.width /
                                                                            2 -
                                                                        0.5
                                                                    }%`,
                                                                    top: `${
                                                                        box.y -
                                                                        box.height /
                                                                            2 -
                                                                        0.5
                                                                    }%`,
                                                                    width: `${
                                                                        box.width +
                                                                        1
                                                                    }%`,
                                                                    height: `${
                                                                        box.height +
                                                                        1
                                                                    }%`,
                                                                }}
                                                            >
                                                                {selectedAnnotation?.id ===
                                                                    annotation.id && (
                                                                    <div className="absolute inset-0">
                                                                        <span className="relative flex h-full w-full">
                                                                            <span
                                                                                className="absolute inline-flex h-full w-full border-2 border-blue-800 bg-blue-500/10 opacity-75 cursor-pointer"
                                                                                onClick={(
                                                                                    e
                                                                                ) =>
                                                                                    handleBoxClick(
                                                                                        e,
                                                                                        annotation
                                                                                    )
                                                                                }
                                                                            ></span>
                                                                        </span>
                                                                    </div>
                                                                )}
                                                                <div
                                                                    className={`absolute w-6 h-6 flex items-center justify-center text-white font-bold rounded-full cursor-pointer z-10 
                                                                    ${
                                                                        selectedAnnotation?.id ===
                                                                        annotation.id
                                                                            ? "bg-gray-400"
                                                                            : getCircleColor(
                                                                                  annotation.reviewStatus
                                                                              )
                                                                    }`}
                                                                    style={{
                                                                        top: "-8px",
                                                                        left: `${
                                                                            4 +
                                                                            index *
                                                                                6
                                                                        }px`,
                                                                        transform:
                                                                            "translate(-50%, -50%)",
                                                                    }}
                                                                    onClick={(
                                                                        e
                                                                    ) =>
                                                                        handleDotClick(
                                                                            e,
                                                                            annotation
                                                                        )
                                                                    }
                                                                >
                                                                    <span className="text-xs">
                                                                        {index +
                                                                            1}
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        )
                                                    )
                                                )}
                                        </div>
                                    </Page>
                                </Document>
                            </div>
                        </div>

                        {/* Common pagination controls centered across both PDFs */}
                        <div className="absolute bottom-0 left-0 right-[320px] bg-white border-t border-gray-200 z-50 h-16">
                            <div className="flex justify-between items-center h-full px-4">
                                <div className="flex-1 text-center flex justify-center items-center gap-4">
                                    <button
                                        onClick={() => handlePageChange(-1)}
                                        disabled={currentPage <= 1}
                                        className="flex items-center gap-2 px-3 py-2 bg-white rounded-full shadow-md hover:bg-gray-100 disabled:opacity-50"
                                    >
                                        <ChevronLeft size={20} />
                                        Previous
                                    </button>
                                    <span className="text-gray-600">
                                        Page {currentPage} of {numPages}
                                    </span>
                                    <button
                                        onClick={() => handlePageChange(1)}
                                        disabled={
                                            currentPage >= (numPages || 1)
                                        }
                                        className="flex items-center gap-2 px-3 py-2 bg-white rounded-full shadow-md hover:bg-gray-100 disabled:opacity-50"
                                    >
                                        Next
                                        <ChevronRight size={20} />
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Right Panel - Annotations */}
                        <div className="w-80 h-full bg-gray-50 overflow-y-auto">
                            <div className="sticky top-0 bg-gray-50 p-4 border-b border-gray-200">
                                <div className="flex items-center justify-between mb-3">
                                    <h2 className="text-xl font-semibold">
                                        Validation Status
                                    </h2>
                                    <div className="relative filter-dropdown">
                                        <button
                                            onClick={() =>
                                                setIsFilterOpen(!isFilterOpen)
                                            }
                                            className="text-sm border rounded-md px-2 py-1 bg-white flex items-center gap-2"
                                        >
                                            Filter
                                            <span className="text-xs bg-gray-100 px-1.5 py-0.5 rounded-full">
                                                {selectedStatuses.length ||
                                                    "All"}
                                            </span>
                                        </button>

                                        {isFilterOpen && (
                                            <div className="absolute right-0 top-full mt-1 bg-white border rounded-md shadow-lg z-50 min-w-[200px]">
                                                <div className="p-2">
                                                    <label className="flex items-center gap-2 px-2 py-1 hover:bg-gray-50 cursor-pointer border-b">
                                                        <input
                                                            type="checkbox"
                                                            checked={
                                                                selectedStatuses.length ===
                                                                REVIEW_STATUSES.length
                                                            }
                                                            onChange={(e) => {
                                                                if (
                                                                    e.target
                                                                        .checked
                                                                ) {
                                                                    setSelectedStatuses(
                                                                        [
                                                                            ...REVIEW_STATUSES,
                                                                        ]
                                                                    );
                                                                } else {
                                                                    setSelectedStatuses(
                                                                        []
                                                                    );
                                                                }
                                                            }}
                                                            className="rounded"
                                                        />
                                                        <span className="text-sm font-medium">
                                                            All
                                                        </span>
                                                    </label>
                                                    {REVIEW_STATUSES.map(
                                                        (status) => (
                                                            <label
                                                                key={status}
                                                                className="flex items-center gap-2 px-2 py-1 hover:bg-gray-50 cursor-pointer"
                                                            >
                                                                <input
                                                                    type="checkbox"
                                                                    checked={selectedStatuses.includes(
                                                                        status
                                                                    )}
                                                                    onChange={(
                                                                        e
                                                                    ) => {
                                                                        if (
                                                                            e
                                                                                .target
                                                                                .checked
                                                                        ) {
                                                                            setSelectedStatuses(
                                                                                [
                                                                                    ...selectedStatuses,
                                                                                    status,
                                                                                ]
                                                                            );
                                                                        } else {
                                                                            setSelectedStatuses(
                                                                                selectedStatuses.filter(
                                                                                    (
                                                                                        s
                                                                                    ) =>
                                                                                        s !==
                                                                                        status
                                                                                )
                                                                            );
                                                                        }
                                                                    }}
                                                                    className="rounded"
                                                                />
                                                                <span className="text-sm">
                                                                    {status}
                                                                </span>
                                                            </label>
                                                        )
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                            <div className="p-4 space-y-4">
                                {filteredAnnotations
                                    .filter((ann) => ann.page === currentPage)
                                    .map((annotation, index) => (
                                        <div
                                            key={annotation.id}
                                            ref={(el) =>
                                                (annotationRefs.current[
                                                    annotation.id
                                                ] = el)
                                            }
                                            className={`p-3 bg-white rounded-lg cursor-pointer transition-all
                                                hover:bg-gray-50
                                                ${
                                                    selectedAnnotation?.id ===
                                                    annotation.id
                                                        ? "border-l-4 border-red-500 bg-gray-50"
                                                        : ""
                                                }`}
                                            onClick={() => {
                                                if (
                                                    selectedAnnotation?.id ===
                                                    annotation.id
                                                ) {
                                                    setSelectedAnnotation(null);
                                                } else {
                                                    setSelectedAnnotation(
                                                        annotation
                                                    );
                                                }
                                            }}
                                        >
                                            <div className="flex flex-col">
                                                <div className="flex items-center justify-between mb-1">
                                                    <div className="text-xs text-gray-500">
                                                        Item {index + 1}
                                                    </div>
                                                    {annotation.reviewStatus && (
                                                        <span
                                                            className={`inline-block text-xs font-semibold px-2.5 py-0.5 rounded ${getBadgeColor(
                                                                annotation.reviewStatus
                                                            )}`}
                                                        >
                                                            {
                                                                annotation.reviewStatus
                                                            }
                                                        </span>
                                                    )}
                                                </div>
                                                {annotation.commentText && (
                                                    <div className="text-sm font-medium text-gray-800">
                                                        {annotation.commentText}
                                                    </div>
                                                )}
                                                {selectedAnnotation?.id ===
                                                    annotation.id && (
                                                    <div className="mt-3 pt-3 border-t border-gray-200">
                                                        <div className="text-sm text-gray-600">
                                                            {annotation.rationale && (
                                                                <div className="mb-2">
                                                                    <strong>
                                                                        Rationale:
                                                                    </strong>
                                                                    <CustomReactMarkdown>
                                                                        {
                                                                            annotation.rationale
                                                                        }
                                                                    </CustomReactMarkdown>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                {filteredAnnotations.filter(
                                    (annotation) =>
                                        annotation.page === currentPage
                                ).length === 0 && (
                                    <div className="text-gray-500 text-center py-4">
                                        No comments or discrepancies present on
                                        this page.
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CommentValidationView;
